import { checkForAndReplaceNullEmptyWhitespace, convertToTitleCase, formatPriceWithPrefix } from "utils/string";
import { getFileNameBasedOnCurrentDateTime } from "utils/file";
import { AcknowledgementPriceFormat, AcknowledgementPriceFormatType, OrderType } from "types/declarations/enums.ts";
import { UnavailableDataPlaceholderText } from "constants/text";
import { formatDateToMMDDYYYY } from "utils/date";
import * as Excel from "exceljs";
import FileSaver from "file-saver";
import { models } from "types/api/viewModels.ts";
import { getOrderAccountInfo } from "pages/OrderPages/utils/OrderDetailUtil.ts";
import { getOrderTypeDisplayLabel } from "utils/order";

const OrderNameText = "Order Name";

const getOrderItemsCount = (orderDetails: models["OrderDetailViewModel"]) =>
	orderDetails.configurations?.reduce((count, configuration) => count + (configuration.lineItems?.length ?? 0), 0) ??
	0;

const isMerchOrder = (orderDetails: models["OrderDetailViewModel"]) => orderDetails.orderType === OrderType.MERCH;

export function convertOrderDetailsToExcelOrderDetails(
	orderDetails: models["OrderDetailViewModel"],
	format?: AcknowledgementPriceFormatType
) {
	if (orderDetails) {
		const accountInformation = getOrderAccountInfo(orderDetails);
		const orderTotal =
			!format || format === AcknowledgementPriceFormat.LIST
				? { Subtotal: formatPriceWithPrefix(orderDetails.pricing?.listPrice) }
				: { "Net Total": formatPriceWithPrefix(orderDetails.pricing?.finalNetPrice) };
		return [
			{
				"Account Number": checkForAndReplaceNullEmptyWhitespace(accountInformation.accountNumber),
				"PO Number": checkForAndReplaceNullEmptyWhitespace(orderDetails.poNumber),
				"Confirmation Number": checkForAndReplaceNullEmptyWhitespace(orderDetails.confirmationNumber),
				"Order Number": checkForAndReplaceNullEmptyWhitespace(orderDetails.orderNumber),
				...(isMerchOrder(orderDetails)
					? {
							[OrderNameText]: checkForAndReplaceNullEmptyWhitespace(orderDetails.jobName),
							Items: getOrderItemsCount(orderDetails)
						}
					: {
							"Job Name": checkForAndReplaceNullEmptyWhitespace(orderDetails.jobName),
							"Designer Number": checkForAndReplaceNullEmptyWhitespace(
								orderDetails.designer?.designerNumber
							)
						}),
				"Order Type": checkForAndReplaceNullEmptyWhitespace(
					getOrderTypeDisplayLabel(orderDetails.orderType).toUpperCase()
				),
				"Order Status": !orderDetails.orderType?.includes(OrderType.BILLING_ONLY)
					? checkForAndReplaceNullEmptyWhitespace(convertToTitleCase(orderDetails.status).toUpperCase())
					: UnavailableDataPlaceholderText,
				"Order Date": orderDetails.orderDate
					? formatDateToMMDDYYYY(new Date(orderDetails.orderDate))
					: UnavailableDataPlaceholderText,
				"Estimated Delivery Date": !orderDetails.orderType?.includes(OrderType.BILLING_ONLY)
					? checkForAndReplaceNullEmptyWhitespace(orderDetails.eta)
					: UnavailableDataPlaceholderText,
				...(!isMerchOrder(orderDetails) && {
					"Cab #": orderDetails.counts?.cabinetCount ?? UnavailableDataPlaceholderText,
					"Cube #": orderDetails.counts?.cubeCount ?? UnavailableDataPlaceholderText,
					"Acc #": orderDetails.counts?.accessoryCount ?? UnavailableDataPlaceholderText
				}),
				...orderTotal
			}
		];
	}

	return [];
}

function getPricingDetails(
	lineItem: models["LineItemViewModel"],
	includeAcknowledgementPricing: boolean,
	format?: AcknowledgementPriceFormatType,
	multiplier?: number | null
) {
	if (!includeAcknowledgementPricing) {
		return {
			"List Price": formatPriceWithPrefix(lineItem.pricing?.adjustedListPrice ?? lineItem.pricing?.listPrice)
		};
	}

	const basePricingObject = {
		"List Price": formatPriceWithPrefix(lineItem.pricing?.listPrice),
		"Adj. List Price": formatPriceWithPrefix(lineItem.pricing?.adjustedListPrice)
	};

	if (!format || format === AcknowledgementPriceFormat.LIST) {
		return {
			...basePricingObject,
			"Extended Price": formatPriceWithPrefix(
				lineItem.pricing?.extendedAdjustedListPrice ?? lineItem.pricing?.extendedListPrice
			)
		};
	}

	return {
		...basePricingObject,
		"Ext. Net Price": formatPriceWithPrefix(
			lineItem.pricing?.extendedAdjustedNetPrice ?? lineItem.pricing?.extendedNetPrice
		),
		"Final Price": formatPriceWithPrefix(lineItem.pricing?.finalNetPrice),
		...(format === AcknowledgementPriceFormat.NET_MULTIPLIER &&
			multiplier &&
			multiplier !== 1 && { Multiplier: multiplier })
	};
}

export function convertOrderDetailsToExcelLineItems(
	orderDetails: models["OrderDetailViewModel"],
	includeAcknowledgementPricing: boolean,
	format?: AcknowledgementPriceFormatType
) {
	const lineItems = orderDetails?.configurations?.map?.((config) => config.lineItems)?.flat();
	const isMerch = isMerchOrder(orderDetails);

	if (lineItems && lineItems.length > 0) {
		return orderDetails?.configurations
			?.map?.((config) =>
				config?.lineItems?.map((od) => {
					const pricingDetails = getPricingDetails(
						od,
						includeAcknowledgementPricing,
						format,
						config.multiplier
					);
					return {
						"Line Item Number": checkForAndReplaceNullEmptyWhitespace(od?.lineItemNumber),
						"Quantity Ordered": checkForAndReplaceNullEmptyWhitespace(od?.quantityOrdered),
						"Quantity Shipped": checkForAndReplaceNullEmptyWhitespace(od?.quantityShipped),
						"Quantity Backordered": checkForAndReplaceNullEmptyWhitespace(od?.quantityBackordered),
						"Part/SKU": checkForAndReplaceNullEmptyWhitespace(od?.sku),
						Description: checkForAndReplaceNullEmptyWhitespace(od?.description),
						...(!isMerch && {
							"Door Style": checkForAndReplaceNullEmptyWhitespace(config.globals?.style?.description),
							Finish:
								orderDetails?.orderType === OrderType.CUSTOM_COLOR
									? checkForAndReplaceNullEmptyWhitespace(
											`${config.globals?.finish?.customColor?.vendorDescription} - ${config.globals?.finish?.customColor?.colorDescription}`
										)
									: checkForAndReplaceNullEmptyWhitespace(config.globals?.finish?.description)
						}),
						...(!isMerch && od?.hinge && { Hinge: checkForAndReplaceNullEmptyWhitespace(od?.hinge) }),
						...(!isMerch && { Modification: modificationsToString(od?.modifications) }),
						"Ship Date": od?.shipDate
							? formatDateToMMDDYYYY(new Date(od?.shipDate))
							: UnavailableDataPlaceholderText,
						"Truck Number": checkForAndReplaceNullEmptyWhitespace(
							od?.truckNumber ?? orderDetails.shippingDetails?.truckNumber
						),
						"Tracking Number": checkForAndReplaceNullEmptyWhitespace(
							od?.trackingNumber ?? orderDetails.shippingDetails?.trackingNumber
						),
						Carrier: checkForAndReplaceNullEmptyWhitespace(
							od?.carrierName ?? orderDetails.shippingDetails?.carrierName
						),
						...pricingDetails
					};
				})
			)
			.flat();
	}

	return [];
}

export const modificationsToString = (mod: models["PendingModificationViewModel"][] | undefined | null) => {
	let result = "";

	if (mod && mod.length > 0) {
		mod.forEach((item) => {
			result += `${checkForAndReplaceNullEmptyWhitespace(item.description)}, ${item.values?.map((value) => checkForAndReplaceNullEmptyWhitespace(value.value)).join(", ")}; `;
		});
	} else {
		result = "N/A";
	}

	return result;
};

async function handleCellProtection(
	detailsWorksheet: Excel.Worksheet,
	lineItemsWorksheet: Excel.Worksheet,
	includeAcknowledgementPricing: boolean,
	orderDetails: models["OrderDetailViewModel"]
) {
	if (!includeAcknowledgementPricing) return;
	detailsWorksheet.eachRow((row) => {
		row.eachCell((cell) => {
			cell.protection = { locked: false, hidden: false };
		});
	});
	const subtotalIndex = detailsWorksheet.columns.length - 1;
	detailsWorksheet.columns.forEach((column, colIdx) => {
		if (colIdx === subtotalIndex && column !== undefined) {
			// @ts-ignore
			column.eachCell((cell) => {
				cell.protection = { locked: true };
			});
		}
	});

	const protectionOptions = { selectLockedCells: true };
	const password = `${orderDetails.orderId}-${orderDetails.poNumber}`;
	await detailsWorksheet.protect(password, protectionOptions);

	lineItemsWorksheet.eachRow((row) => {
		row.eachCell((cell) => {
			cell.protection = { locked: false, hidden: false };
		});
	});
	const protectedLineItemColumns = [
		"List Price",
		"Adj. List Price",
		"Extended Price",
		"Ext. Net Price",
		"Final Price"
	];
	const protectedLineItemIndices = lineItemsWorksheet.columns
		.map((col, idx) => (protectedLineItemColumns.includes(col.header as string) ? idx : -1))
		.filter((idx) => idx !== -1);

	lineItemsWorksheet.columns.forEach((column, colIdx) => {
		if (protectedLineItemIndices.includes(colIdx) && column !== undefined) {
			// @ts-ignore
			column.eachCell((cell) => {
				cell.protection = { locked: true };
			});
		}
	});

	await lineItemsWorksheet.protect(password, protectionOptions);
}

function filterAdjListPriceIfEmpty(
	data: (Record<string, any> | undefined)[],
	columnName: string
): (Record<string, any> | undefined)[] {
	if (!data || data.length === 0) return data;

	const hasNonEmptyValue = data.some(
		(row) =>
			row?.[columnName] !== null &&
			row?.[columnName] !== undefined &&
			row?.[columnName] !== "" &&
			row?.[columnName] != UnavailableDataPlaceholderText
	);

	if (hasNonEmptyValue) return data;

	return data.map((row) => {
		const filtered = { ...row };
		delete filtered[columnName];
		return filtered;
	});
}

export async function generateMyOrderDetailsExcel(
	orderDetails: models["OrderDetailViewModel"],
	includeAcknowledgementPricing: boolean,
	format?: AcknowledgementPriceFormatType
) {
	const filePrefix = orderDetails?.jobName
		? `${orderDetails.jobName}_PO${orderDetails.poNumber}`
		: `PO${orderDetails.poNumber}`;

	const workbook = new Excel.Workbook();
	const detailsWorksheet = workbook.addWorksheet("Order");
	const lineItemsWorksheet = workbook.addWorksheet("Line Items");

	const details = convertOrderDetailsToExcelOrderDetails(orderDetails, format);

	if (details.length > 0) {
		detailsWorksheet.columns = Object.keys(details[0] ?? {}).map((col) => {
			return { key: col, header: col };
		});
		detailsWorksheet.addRows(details);
	}

	const lineItems = convertOrderDetailsToExcelLineItems(orderDetails, includeAcknowledgementPricing, format);

	if (lineItems && lineItems.length > 0) {
		const filteredLineItems = filterAdjListPriceIfEmpty(lineItems, "Adj. List Price");
		lineItemsWorksheet.columns = Object.keys(filteredLineItems[0] ?? {}).map((col) => {
			return { key: col, header: col };
		});
		lineItemsWorksheet.addRows(lineItems);
	}

	await handleCellProtection(detailsWorksheet, lineItemsWorksheet, includeAcknowledgementPricing, orderDetails);

	try {
		workbook.xlsx.writeBuffer().then((data) => {
			const blob = new Blob([data], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
			});
			FileSaver.saveAs(blob, `${getFileNameBasedOnCurrentDateTime(filePrefix)}.xlsx`);
		});
	} catch (e) {
		console.error("Error trying to generate order details xlsx file", e);
	}
}
